import {
  APIQueryRecordsQueryResponse,
  APIRecord,
} from "server/services/tables/types/records";
import { Button } from "blocks/components";
import BlockWrapper from "blocks/components/BlockWrapper";
import { DisembodiedHeader } from "blocks/components/DisembodiedHeader";
import { BlockId } from "blocks/types";
import { Spinner } from "blocks/components/Spinner";
import { usePageContext } from "lib/context/page-context";
import { useTheme } from "lib/theme";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InfiniteData } from "@tanstack/react-query";
import { Order, RecordQuery } from "server/schemas/table-records";
import { trpc } from "utils/trpc";
import { Table } from "../";
import { ModalEditTableRecord } from "./ModalEditTableRecord";
import { MenuButtonProps } from "./MenuButton";
import { TableView } from "./TableView";
import { FILTERABLE_FIELD_TYPES } from "./utils";
import { sortBy } from "lodash";
import { toast } from "blocks/components/Notification";
import { ZeroState } from "./ZeroState";
import { ModalDeleteTableRecord } from "./ModalDeleteTableRecord";
import { TableBlock } from "../types";
import { useTrackingContext } from "observability";
import { ErrorBoundary } from "react-error-boundary";
import { InformativeBlockErrorState } from "blocks/components/BlockErrors/InformativeBlockErrorState";
import { TRPCClientError } from "@trpc/client";

const QUERY_LIMIT = 20;

export function TableBlock(props: { block: TableBlock; blockId: BlockId }) {
  const { config } = props.block;
  const theme = useTheme();
  const { isEditing } = usePageContext();

  return (
    <BlockWrapper
      blockId={props.blockId}
      block={props.block}
      maxWidth={
        config.style?.width
          ? theme.app.width[config.style?.width]
          : theme.app.width.wide
      }
      alignment={config.style?.alignment}
    >
      <ErrorBoundary
        fallbackRender={({ error }) => {
          // If we're in the editor and the error is a permissions error, show a
          // special error state to help the builder fix the issue. Otherwise,
          // rethrow the error to let the standard block error state render.
          if (
            isEditing &&
            error instanceof TRPCClientError &&
            error.data.code === "tables:permissions:errors"
          ) {
            return (
              <InformativeBlockErrorState
                title="Data not available"
                description="This component is connected to a Table you don't have access to."
                iconBefore="navTable"
                iconAfter="formAdd"
              />
            );
          } else {
            throw error;
          }
        }}
      >
        <TableBlockInner {...props} />
      </ErrorBoundary>
    </BlockWrapper>
  );
}

export function TableBlockInner(props: {
  block: TableBlock;
  blockId: BlockId;
}) {
  const { config } = props.block;
  const { emitConsumerInteractionEvent } = useTrackingContext();
  const { pageId, projectId, accountId, isEditing, isThumbnail, isEmbedded } =
    usePageContext();

  const [deleteRecordModalData, setDeleteRecordModalData] = useState({
    open: false,
    recordId: "",
  });

  const tableId = props.block.config.tableId;

  const [order, setOrder] = useState<Order>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>();
  const [editingRecordId, setEditingRecordId] = useState<
    APIRecord["id"] | "NEW"
  >();

  const isEditingRecord = editingRecordId !== undefined;

  const recordQuery: RecordQuery = useMemo(() => {
    const query: RecordQuery = {};

    if (order) query.orders = [order];
    if (search && filter)
      query.filters = [
        {
          key: filter,
          value: search,
          operator: "contains",
        },
      ];

    query.limit = QUERY_LIMIT;

    return query;
  }, [filter, order, search]);

  const { data: fieldsResponse, isLoading: isLoadingFields } =
    trpc.tableFields.list.useQuery(
      {
        tableId: tableId as string,
      },
      {
        enabled: !!tableId && !!accountId,
        suspense: false,
      }
    );

  const fields = useMemo(() => {
    const [supportedFields] = Table.filterUnsupportedTableFields(
      fieldsResponse?.data
    );
    const fields = supportedFields?.filter(
      (field) => !props.block.config.hiddenFields?.includes(`f${field.id}`)
    );

    if (fields) {
      if (
        props.block.config.columnOrder &&
        props.block.config.columnOrder.length > 0
      ) {
        return sortBy(fields, (field) =>
          props.block.config?.columnOrder?.indexOf(field.id) === -1
            ? Number.MAX_SAFE_INTEGER
            : props.block.config?.columnOrder?.indexOf(field.id)
        );
      }
      return sortBy(fields, (field) => field.options?.order);
    }
  }, [
    fieldsResponse,
    props.block.config.hiddenFields,
    props.block.config.columnOrder,
  ]);

  const {
    data: currentRecordsResponse,
    isLoading: isLoadingRecords,
    fetchNextPage,
    isFetchingNextPage,
  } = trpc.blockTable.queryRecords.useInfiniteQuery(
    {
      tableId: tableId as string,
      filter: recordQuery,
      blockId: config.id as string,
    },
    {
      enabled: !!tableId && !!accountId && !!config.id,
      suspense: false,
      getNextPageParam: (lastPage) => ({
        query_type: "window",
        start_cursor: lastPage.meta?.pagination.end_cursor,
      }),
    }
  );

  const utils = trpc.useContext();

  const createRecordMutation = trpc.blockTable.createRecord.useMutation({
    onSuccess() {
      toast.success("Record created successfully.");
      emitConsumerInteractionEvent({
        interaction_goal: "Create table record",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "datagrid",
        embedded_flag: isEmbedded,
      });
      utils.blockTable.queryRecords.refetch();
      utils.blockKanban.get.refetch();
      setEditingRecordId(undefined);
    },
  });

  const updateRecordMutation = trpc.blockTable.updateRecord.useMutation({
    onSuccess() {
      toast.success("Record updated successfully.");
      emitConsumerInteractionEvent({
        interaction_goal: "Edit table record",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "datagrid",
        embedded_flag: isEmbedded,
      });
      utils.blockTable.queryRecords.refetch();
      utils.blockKanban.get.refetch();
      setEditingRecordId(undefined);
    },
  });

  const deleteRecordMutation = trpc.blockTable.deleteRecord.useMutation({
    onSuccess(data, variables) {
      toast.success("Record successfully deleted");
      emitConsumerInteractionEvent({
        interaction_goal: "Delete table record",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "datagrid",
        embedded_flag: isEmbedded,
      });
      utils.blockTable.queryRecords.setInfiniteData(
        {
          tableId: tableId as string,
          filter: recordQuery,
          blockId: config.id as string,
        },
        (data) => {
          if (!data) {
            return {
              pages: [],
              pageParams: [],
            };
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              data: page.data.filter(
                (record) => !variables.body.record_ids.includes(record.id)
              ),
            })),
          };
        }
      );
    },
  });

  const isSubmitting =
    createRecordMutation.isLoading ||
    updateRecordMutation.isLoading ||
    deleteRecordMutation.isLoading;

  const lastRecordsResponse =
    useRef<InfiniteData<APIQueryRecordsQueryResponse>>();
  if (currentRecordsResponse !== undefined)
    lastRecordsResponse.current = currentRecordsResponse;

  const recordsResponse = currentRecordsResponse ?? lastRecordsResponse.current;

  const records = useMemo(() => {
    return recordsResponse?.pages?.flatMap?.((e) => e.data);
  }, [recordsResponse?.pages]);

  const filterOptions = useMemo(() => {
    if (!fields) return [];

    return fields
      .filter((field) => FILTERABLE_FIELD_TYPES.includes(field.type))
      .map((field) => ({ value: `f${field.id}`, label: field.name }));
  }, [fields]);

  const showDeleteRecordModal = useCallback((id: string) => {
    setDeleteRecordModalData({
      open: true,
      recordId: id,
    });
    return true;
  }, []);

  const hideDeleteRecordModal = () => {
    setDeleteRecordModalData({
      open: false,
      recordId: "",
    });
  };

  const handleDelete = useMemo(() => {
    if (props.block.config.canDeleteRecords)
      return (id: string) => {
        if (config.id === undefined || tableId === undefined) return false;

        deleteRecordMutation.mutate({
          tableBlockId: config.id as string,
          tableId,
          body: { record_ids: [id] },
        });

        return true;
      };
  }, [
    config.id,
    deleteRecordMutation,
    props.block.config.canDeleteRecords,
    tableId,
  ]);

  const handleRecordSubmit = useCallback(
    (data: APIRecord["data"]) => {
      if (!editingRecordId || config.id === undefined || tableId === undefined)
        return;

      if (editingRecordId === "NEW" && props.block.config.canCreateRecords) {
        createRecordMutation.mutate({
          tableId,
          body: { new_records: [{ data: data }] },
          tableBlockId: config.id as string,
        });
      } else if (!!editingRecordId && props.block.config.canUpdateRecords) {
        updateRecordMutation.mutate({
          tableId,
          body: { updated_records: [{ id: editingRecordId, data: data }] },
          tableBlockId: config.id as string,
        });
      }
    },
    [
      editingRecordId,
      tableId,
      props.block.config.canCreateRecords,
      props.block.config.canUpdateRecords,
      createRecordMutation,
      updateRecordMutation,
      config.id,
    ]
  );

  const menuItems = useMemo(() => {
    const menuItems: MenuButtonProps["menuItems"] = [];

    if (props.block.config.canUpdateRecords) {
      menuItems.push({
        title: "Edit",
        onClick: (recordId) => {
          setEditingRecordId(recordId);
          return true;
        },
      });
    }

    if (handleDelete) {
      menuItems.push({
        title: "Delete",
        onClick: showDeleteRecordModal,
        color: "danger",
      });
    }

    return menuItems;
  }, [
    handleDelete,
    props.block.config.canUpdateRecords,
    showDeleteRecordModal,
  ]);

  useEffect(() => {
    if (filterOptions.length === 1 || (filterOptions.length > 1 && !filter))
      setFilter(filterOptions[0].value);
  }, [filter, filterOptions, setFilter]);

  const isLoading = isLoadingFields || (isLoadingRecords && !recordsResponse);

  const renderBody = () => {
    if (!tableId) {
      return <ZeroState title="No table selected" />;
    }

    if (!isLoading && records?.length === 0) {
      return (
        <ZeroState
          title="No records to display"
          subtitle="There are no records in the connected data source."
        />
      );
    }

    if (isLoading) return <Spinner height={200} />;
    if (!fields || !records) return <div>Something&apos;s wrong...</div>;

    return (
      <TableView
        accountId={accountId}
        tableId={tableId}
        fields={fields}
        records={records}
        order={order}
        onOrderChange={setOrder}
        menuItems={menuItems}
        disableScroll={isEditing || !!isThumbnail}
        isDisabled={isThumbnail}
        canUpdateRecord={props.block.config.canUpdateRecords}
        tableBlockId={config.id}
      />
    );
  };

  const renderLoadMoreButton = () => {
    const pages = recordsResponse?.pages;
    const hasMore = pages?.[pages.length - 1]?.meta?.pagination.has_more;
    if (!pages?.length || !hasMore) return null;
    return (
      <Button
        className="w-full"
        onClick={() => fetchNextPage()}
        isLoading={isFetchingNextPage}
        color="secondary"
        disabled={isThumbnail}
      >
        Load more
      </Button>
    );
  };

  return (
    <Fragment>
      {!!(
        props.block.config.title ||
        props.block.config.canSearchRecords ||
        props.block.config.canCreateRecords
      ) && (
        <DisembodiedHeader
          renderRight={() => (
            <div className="flex flex-nowrap items-center gap-2.5">
              {props.block.config.canCreateRecords && (
                <Button
                  onClick={() => setEditingRecordId("NEW")}
                  size="small"
                  disabled={isThumbnail}
                >
                  {props.block.config.newRecordButtonCopy ||
                    Table.strings.NEW_RECORD_BUTTON_DEFAULT_COPY}
                </Button>
              )}
            </div>
          )}
        >
          {props.block.config.title}
        </DisembodiedHeader>
      )}
      <div className="rounded-xl bg-zi-white p-8 shadow-xl">
        {renderBody()}
        {renderLoadMoreButton()}
        {deleteRecordModalData.open && (
          <ModalDeleteTableRecord
            onClosed={() => hideDeleteRecordModal()}
            onDelete={() => {
              handleDelete && handleDelete(deleteRecordModalData.recordId);
              hideDeleteRecordModal();
            }}
          />
        )}
      </div>
      {isEditingRecord && fields && records && (
        <ModalEditTableRecord
          newRecordButtonCopy={props.block.config.newRecordButtonCopy}
          fields={fields}
          record={records.find((record) => record.id === editingRecordId)}
          onSubmit={handleRecordSubmit}
          onClosed={() => setEditingRecordId(undefined)}
          isSubmitting={isSubmitting}
          tableId={tableId}
        />
      )}
    </Fragment>
  );
}
